/**
 * Mide la calidad de la conexion del puesto de trabajo y decide si el indicador
 * de "Conexion inestable" se enciende.
 *
 * POR QUE EXISTE
 *
 * El 1-oct-2026 Cotorra reporto lentitud al llenar historias y toda la
 * infraestructura estaba ociosa: CPU de la instancia en 0,2-6%, la base con 2-6
 * conexiones de 60, la app respondiendo en 99 ms por la misma ruta que San
 * Francisco. Lo que fallaba era el enlace de la sede, que perdia 6,9% de los
 * paquetes (maximo 1.033 ms, jitter 111 ms) contra 0,0% (85 ms, jitter 0,8 ms)
 * desde otra red al mismo servidor. El 23-sep ya perdia 2,1%: venia
 * degradandose y nadie lo vio hasta que reclamaron.
 *
 * Ninguna metrica del servidor podia ver eso. Solo el navegador del puesto
 * puede, y resulta que ya tiene los datos: `PerformanceResourceTiming` entrega
 * los tiempos desglosados de cada peticion. Nunca los habiamos recogido.
 *
 * COMO MIDE
 *
 * Pasivo, sobre el trafico que la aplicacion ya genera. No hay sonda ni ping
 * periodico a proposito: el sintoma de ese dia era el formulario de historia
 * clinica haciendo muchas peticiones seguidas, donde cada perdida cuesta
 * cientos de ms de retransmision TCP; una sonda sintetica habria visto la
 * latencia variar sin capturar ese efecto acumulado. Y meterle trafico extra a
 * un enlace que ya esta saturado empeora justo lo que se quiere medir.
 *
 * POR QUE NO MIDE PERDIDA DE PAQUETES
 *
 * El navegador no puede: no hay ICMP ni acceso a la pila TCP. Pero el efecto si
 * se ve. La perdida deja dos huellas: peticiones que fallan, y dispersion en el
 * TTFB (unas normales y otras con retransmision). Esas dos son lo que se mide.
 */

import Vue from 'vue';

/** Tamano de la ventana de evaluacion. */
const VENTANA_MS = 60000;

/**
 * Minimo de peticiones para opinar.
 *
 * Alguien mirando una pantalla quieto no genera trafico, y no se quiere que el
 * indicador concluya nada con tres muestras.
 */
const MINIMO_PETICIONES = 10;

/** Porcentaje de fallos que enciende el indicador. Cotorra con el enlace malo: 6,9%; red sana: 0,0%. */
const UMBRAL_FALLOS_PCT = 5;

/**
 * Umbral absoluto de dispersion, en ms.
 *
 * Evita alarmar en una red lenta pero *estable*, donde el usuario no percibe
 * saltos. Cotorra ese dia llegaba a 1.033 ms de maximo.
 */
const UMBRAL_P95_MS = 1000;

/**
 * Cuantas veces el p95 debe superar al p50 para considerarlo inestable.
 *
 * Esta es la condicion que distingue la red del servidor sin tener que separar
 * los tiempos con precision, y es la razon por la que el indicador no se
 * enciende cuando el problema es nuestro:
 *
 *   red sana            p50    85 ms  p95    95 ms  ratio  1,1   no prende
 *   Cotorra 1-oct       p50    85 ms  p95 1.033 ms  ratio 12,2   PRENDE
 *   servidor lento      p50 2.400 ms  p95 2.900 ms  ratio  1,2   no prende
 *   red lenta y estable p50   600 ms  p95   700 ms  ratio  1,2   no prende
 *
 * Una red con perdida produce dispersion; un servidor lento es lento de forma
 * uniforme.
 */
const RATIO_P95_P50 = 4;

/**
 * p50 que enciende el indicador por si solo, sin mirar el ratio.
 *
 * El ratio tiene un punto ciego que se vio con datos reales de produccion: una
 * red UNIFORMEMENTE mala se le parece a un servidor lento. En San Francisco
 * aparecio este puesto:
 *
 *   p-3zapu34u   p50 9.600 ms   p95 27.463 ms   ratio 2,9   NO prendia
 *
 * Una MEDIANA de 9,6 segundos —no el pico, la mediana— y el indicador callado,
 * mientras el resto de los puestos de la misma sede respondian en 137 ms. O sea
 * que el peor puesto era justo el que no se marcaba.
 *
 * Con una mediana por encima de 2 s la conexion de ese equipo esta mala y no
 * hace falta discutir la forma de la distribucion. Si fuera el servidor, lo
 * estarian sintiendo todos y se sabria por otras vias.
 */
const UMBRAL_P50_MS = 2000;

/**
 * Cortes de las barras de señal, en ms de p50.
 *
 * Calibrados con la linea base real de produccion, no a ojo: San Francisco sano
 * da p50 137 ms y p95 332 ms en 71 puestos, asi que lo normal cae con holgura en
 * el nivel maximo. El nivel 1 es exactamente la condicion de `inestable`, para
 * que las barras y el aviso no puedan contradecirse.
 */
const CORTES_NIVEL = [
    { nivel: 4, hasta: 300 },
    { nivel: 3, hasta: 800 },
    { nivel: 2, hasta: UMBRAL_P50_MS },
];

/** Ventanas malas seguidas para encender, y buenas seguidas para apagar. */
const VENTANAS_PARA_ENCENDER = 2;
const VENTANAS_PARA_APAGAR = 3;

/** La telemetria no se mide a si misma: si no, una red mala infla su propio conteo de fallos. */
const RUTA_TELEMETRIA = '/api/telemetria-red';

/** Identificador del puesto, para distinguir un wifi malo de un enlace malo. */
const LLAVE_PUESTO = 'sinusalud_puesto_id';

/**
 * Estado que lee el indicador.
 *
 * `Vue.observable` en lugar de Vuex porque esto no es estado de negocio: no se
 * persiste, no lo modifica nadie mas y no tiene por que aparecer en el store.
 */
export const estado = Vue.observable({
    inestable: false,
    /**
     * Nivel de las barras: 1 a 4, o null mientras no hay con que opinar.
     *
     * `null` es un estado de verdad, no un cero disfrazado: hasta que no pase la
     * primera ventana con trafico no sabemos nada, y pintar una barra en ese
     * momento seria inventar.
     */
    nivel: null,
});

let muestras = [];
let fallos = 0;
let total = 0;
let ventanasMalas = 0;
let ventanasBuenas = 0;
let temporizador = null;
let observador = null;
let arrancado = false;

/**
 * Identificador aleatorio y estable por navegador.
 *
 * Hace falta porque la IP publica NO distingue puestos: todos los equipos de una
 * sede salen por la misma, detras del NAT. Y el pendiente que dejo el informe al
 * operador fue precisamente medir puesto por puesto, no solo el enlace.
 *
 * No es dato personal: es un numero al azar. Si el navegador bloquea el
 * almacenamiento, se usa uno de sesion y el dato sigue sirviendo para la sede.
 */
function idPuesto() {
    try {
        let id = window.localStorage.getItem(LLAVE_PUESTO);
        if (!id) {
            id = 'p-' + Math.random().toString(36).slice(2, 10);
            window.localStorage.setItem(LLAVE_PUESTO, id);
        }
        return id;
    } catch (e) {
        return 'p-sin-almacenamiento';
    }
}

function percentil(ordenados, p) {
    if (!ordenados.length) return 0;
    const i = Math.min(ordenados.length - 1, Math.floor((p / 100) * ordenados.length));
    return ordenados[i];
}

/** Lo llama el interceptor de Service.js cuando una peticion no llega a responder. */
export function registrarFallo(url) {
    if (esTelemetria(url)) return;
    fallos++;
    total++;
}

export function registrarExito(url) {
    if (esTelemetria(url)) return;
    total++;
}

function esTelemetria(url) {
    return typeof url === 'string' && url.indexOf(RUTA_TELEMETRIA) !== -1;
}

/**
 * Toma el TTFB de una entrada de rendimiento.
 *
 * `responseStart` llega en 0 cuando el recurso es de otro origen y el servidor
 * no manda `Timing-Allow-Origin`; esas entradas se descartan en vez de
 * contarlas como 0 ms, que ensuciaria el p50 hacia abajo.
 */
function ttfbDe(entrada) {
    if (!entrada.responseStart || !entrada.requestStart) return null;
    const ttfb = entrada.responseStart - entrada.requestStart;
    return ttfb >= 0 ? ttfb : null;
}

function evaluar() {
    const ordenados = muestras.slice().sort((a, b) => a - b);
    const p50 = percentil(ordenados, 50);
    const p95 = percentil(ordenados, 95);
    const pctFallos = total ? (fallos * 100) / total : 0;

    // Se mide sobre las MUESTRAS del observador, no sobre el contador del
    // interceptor.
    //
    // `total` lo cuenta solo el interceptor de `Service.js`, y hay 114 archivos
    // que usan axios directo sin pasar por ahi. En esas pantallas `total` se
    // queda en cero, nunca habia "suficientes" datos y el nivel se quedaba en
    // `null` para siempre: las barras salian grises y no se pintaban nunca.
    //
    // El observador del navegador si ve todas las peticiones, venga de la
    // instancia que venga. Asi que el piso para opinar son las muestras de
    // tiempo; `total` se sigue usando solo para el porcentaje de fallos, que es
    // lo unico que la API de rendimiento no reporta bien.
    const suficientes = muestras.length >= MINIMO_PETICIONES;
    const porFallos = pctFallos >= UMBRAL_FALLOS_PCT;
    const porDispersion = p95 >= UMBRAL_P95_MS && p50 > 0 && p95 >= RATIO_P95_P50 * p50;
    // Lenta de forma sostenida: no depende del ratio, cubre el punto ciego.
    const porLentitud = p50 >= UMBRAL_P50_MS;
    const mala = suficientes && (porFallos || porDispersion || porLentitud);

    // Sin datos suficientes no se cambia de estado: ni se enciende ni se apaga.
    if (suficientes) {
        if (mala) {
            ventanasMalas++;
            ventanasBuenas = 0;
        } else {
            ventanasBuenas++;
            ventanasMalas = 0;
        }

        // Histeresis: sin esto el indicador parpadea con cada pico y la gente
        // deja de creerle.
        if (!estado.inestable && ventanasMalas >= VENTANAS_PARA_ENCENDER) {
            estado.inestable = true;
        } else if (estado.inestable && ventanasBuenas >= VENTANAS_PARA_APAGAR) {
            estado.inestable = false;
        }
    }

    // El nivel sale de la misma evaluacion, asi que cambia como maximo una vez
    // por minuto y no parpadea. El nivel 1 sigue a `inestable`, que ya tiene
    // histeresis, para que las barras y el texto no se contradigan nunca.
    if (suficientes) {
        if (estado.inestable) {
            estado.nivel = 1;
        } else {
            const corte = CORTES_NIVEL.find((c) => p50 < c.hasta);
            estado.nivel = corte ? corte.nivel : 1;
        }
    }

    const resumen = {
        puesto: idPuesto(),
        nivel: estado.nivel,
        peticiones: total,
        fallos: fallos,
        fallos_pct: Number(pctFallos.toFixed(2)),
        ttfb_p50: Math.round(p50),
        ttfb_p95: Math.round(p95),
        ttfb_max: Math.round(ordenados.length ? ordenados[ordenados.length - 1] : 0),
        muestras: muestras.length,
        inestable: estado.inestable,
        motivo: !suficientes ? 'sin_datos' : (porFallos ? 'fallos' : (porDispersion ? 'dispersion' : (porLentitud ? 'lentitud' : 'ok'))),
        pantalla: window.location.hash || window.location.pathname,
        agente: navigator.userAgent,
    };

    muestras = [];
    fallos = 0;
    total = 0;

    return resumen;
}

/**
 * Envia el resumen. Si falla se descarta en silencio: la telemetria no debe
 * estorbar al usuario ni meterle mas trafico a un enlace que ya esta mal. El
 * indicador no depende de esto, se calcula local.
 */
function enviar(resumen, usarBeacon) {
    const cuerpo = JSON.stringify(resumen);
    const url = (process.env.VUE_APP_BASE_PATH || '') + RUTA_TELEMETRIA;

    try {
        if (usarBeacon && navigator.sendBeacon) {
            // `text/plain` y no `application/json` a proposito: con un tipo que
            // no sea simple, el navegador exige un preflight CORS, y un beacon
            // no puede hacerlo. Desde historias (que vive en otro dominio que
            // la API) el envio se bloquearia. El controlador acepta el cuerpo
            // crudo por eso.
            navigator.sendBeacon(url, new Blob([cuerpo], { type: 'text/plain' }));
            return;
        }
        fetch(url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: cuerpo,
            credentials: 'include',
            keepalive: true,
        }).catch(() => {});
    } catch (e) {
        /* en silencio, a proposito */
    }
}

export function iniciar() {
    if (arrancado) return;
    arrancado = true;

    if (typeof PerformanceObserver === 'undefined') {
        // Navegador sin la API: no se mide y no se muestra nada. Preferible a
        // inventar un estado.
        return;
    }

    observador = new PerformanceObserver((lista) => {
        lista.getEntries().forEach((entrada) => {
            if (entrada.initiatorType !== 'xmlhttprequest' && entrada.initiatorType !== 'fetch') return;
            if (esTelemetria(entrada.name)) return;
            const ttfb = ttfbDe(entrada);
            if (ttfb !== null) muestras.push(ttfb);
        });
    });

    // El buffer de resource timing tiene un tope (~250 entradas por defecto) y
    // cuando se llena el navegador DEJA DE REGISTRAR: el observador no recibe
    // nada mas y la medicion se queda ciega sin avisar. Con datos reales de
    // produccion se vio el efecto: de 600 ventanas de San Francisco, 369 traian
    // peticiones contadas por el interceptor pero ni un solo tiempo.
    //
    // Se amplia el tope y, sobre todo, se vacia al cerrar cada ventana (ver el
    // temporizador). Nada mas en la aplicacion usa esta API, asi que vaciarla no
    // le quita informacion a nadie.
    try {
        performance.setResourceTimingBufferSize(1000);
    } catch (e) {
        /* navegador que no lo soporta: queda el tope por defecto */
    }

    try {
        observador.observe({ type: 'resource', buffered: true });
    } catch (e) {
        observador.observe({ entryTypes: ['resource'] });
    }

    temporizador = setInterval(() => {
        // Una ventana sin NADA no se manda.
        //
        // Al principio se enviaba el resumen cada minuto hubiera o no actividad,
        // y en la primera lectura real de produccion las ventanas vacias ahogaron
        // el dato: con 86 puestos en San Francisco la mediana del p50 salia en
        // 0 ms porque la mayoria de los resumenes venian sin una sola muestra.
        // Un usuario con la pantalla abierta y quieto no aporta informacion.
        //
        // Se exige `total === 0 && muestras vacias`: una ventana donde TODAS las
        // peticiones fallaron (total > 0, sin muestras de tiempo) si se manda,
        // porque es justo el caso que interesa.
        if (total === 0 && muestras.length === 0) {
            return;
        }
        enviar(evaluar(), false);

        // Se vacia el buffer para que el navegador siga registrando la ventana
        // siguiente. Sin esto, el tope se llena y dejamos de medir.
        try {
            performance.clearResourceTimings();
        } catch (e) { /* sin consecuencias si no se puede */ }
    }, VENTANA_MS);

    // La ultima ventana es justo la que suele contener el momento en que el
    // usuario se rindio y cerro, asi que se manda con sendBeacon, que sobrevive
    // a la descarga de la pagina.
    window.addEventListener('pagehide', () => {
        if (total === 0 && muestras.length === 0) {
            return;
        }
        enviar(evaluar(), true);
    });
}

export function detener() {
    if (temporizador) clearInterval(temporizador);
    if (observador) observador.disconnect();
    temporizador = null;
    observador = null;
    arrancado = false;
}

export const umbrales = {
    VENTANA_MS,
    MINIMO_PETICIONES,
    UMBRAL_FALLOS_PCT,
    UMBRAL_P95_MS,
    UMBRAL_P50_MS,
    CORTES_NIVEL,
    RATIO_P95_P50,
    VENTANAS_PARA_ENCENDER,
    VENTANAS_PARA_APAGAR,
};
